import type { PostContent } from '../api/createPost'
import { TIKTOK_RESTRICTIONS } from './restrictions'

/**
 * Builds the TikTok PostContent from a workspace media set.
 *
 * TikTok posts are either:
 *   - exactly one video → `video` content type (publishes via /video/init/)
 *   - one or more images → `photo` content type (publishes via /content/init/)
 *
 * Mixed image+video isn't a valid TikTok post on either endpoint and is
 * caught upstream by `workspace-media-validation.ts`. We still defend here.
 */
export const postContent = (
  uploadUrls: { url: string; file: { type: string } }[],
  caption: string,
): PostContent | { type: 'invalid'; reason: string } => {
  if (uploadUrls.length === 0) {
    return { type: 'invalid', reason: 'TikTok posts must have at least one image or video' }
  }

  const allVideo = uploadUrls.every((u) => u.file.type.startsWith('video/'))
  const allImage = uploadUrls.every((u) => u.file.type.startsWith('image/'))

  if (allVideo) {
    if (uploadUrls.length > 1) {
      return { type: 'invalid', reason: 'TikTok video posts must contain exactly one video' }
    }
    return {
      type: 'video',
      caption,
      url: uploadUrls[0]!.url,
    }
  }

  if (allImage) {
    if (uploadUrls.length > TIKTOK_RESTRICTIONS.photo.maxItems) {
      return {
        type: 'invalid',
        reason: `TikTok photo posts allow up to ${TIKTOK_RESTRICTIONS.photo.maxItems} images`,
      }
    }
    // Title is capped at 90 chars; description carries the long-form caption.
    const trimmedTitle = caption.slice(0, TIKTOK_RESTRICTIONS.photo.maxTitleLength)
    return {
      type: 'photo',
      caption,
      title: trimmedTitle,
      description: caption,
      photoImages: uploadUrls.map((u) => u.url),
      photoCoverIndex: 0,
    }
  }

  return {
    type: 'invalid',
    reason: 'TikTok posts cannot mix images and videos',
  }
}
